import React, { useEffect, useState, useCallback } from 'react';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faPen, faSave, faTimes, faLock, faEye, faEyeSlash,
  faEnvelope, faPhone, faBuilding, faCalendarAlt, faCheckCircle,
  faExclamationTriangle, faPercent, faCoins, faShieldAlt, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import ActionOtpModal from '../components/shared/ActionOtpModal';
import { UiPageHeader, UiButton } from '../components/ui';

const inputClass = (enabled: boolean) =>
  `w-full px-4 py-3 border rounded-xl transition-colors ${
    enabled
      ? 'border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500'
      : 'border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400'
  }`;

const ProfilePage: React.FC = () => {
  const { merchant } = useAuth();
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Profile form
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    phone: '',
    usdt_address: '',
    usdt_network: 'TRC20'
  });

  // Action OTP
  const [showProfileOtp, setShowProfileOtp] = useState(false);
  const [showPasswordOtp, setShowPasswordOtp] = useState(false);

  // Primary email
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [primaryEmailVerified, setPrimaryEmailVerified] = useState(false);
  const [primaryEmailInput, setPrimaryEmailInput] = useState('');
  const [primaryEmailOtpSent, setPrimaryEmailOtpSent] = useState(false);
  const [primaryEmailOtp, setPrimaryEmailOtp] = useState('');
  const [primaryEmailCooldown, setPrimaryEmailCooldown] = useState(0);
  const [sendingPrimaryOtp, setSendingPrimaryOtp] = useState(false);
  const [verifyingPrimaryEmail, setVerifyingPrimaryEmail] = useState(false);

  // Password form
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new_password: false,
    confirm: false
  });

  const loadProfile = useCallback(async () => {
    try {
      const response = await merchantApi.get('/merchants/profile');
      setProfile(response.data.data);
      setFormData({
        name: response.data.data.name || '',
        company_name: response.data.data.company_name || '',
        phone: response.data.data.phone || '',
        usdt_address: response.data.data.usdt_address || '',
        usdt_network: response.data.data.usdt_network || 'TRC20'
      });
      setPrimaryEmail(response.data.data.primary_email || '');
      setPrimaryEmailVerified(response.data.data.primary_email_verified || false);
    } catch (error) {
      toast.error(t('profile.loadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    setShowProfileOtp(true);
  };

  const handleSaveProfileVerified = async (actionToken: string) => {
    setSaving(true);
    try {
      await merchantApi.put('/merchants/profile', { ...formData, action_token: actionToken });
      toast.success(t('profile.updateSuccess'));
      setEditing(false);
      loadProfile();
    } catch (error: any) {
      const message = error.response?.data?.message || t('common.error');
      const errors = error.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        errors.forEach((err: string) => toast.error(err));
      } else {
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(t('profile.passwordMismatch'));
      return;
    }
    setShowPasswordOtp(true);
  };

  const handleChangePasswordVerified = async (actionToken: string) => {
    setChangingPassword(true);
    try {
      await merchantApi.post('/merchants/change-password', { ...passwordData, action_token: actionToken });
      toast.success(t('profile.passwordChanged'));
      setShowPasswordForm(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error: any) {
      const message = error.response?.data?.message || t('common.error');
      const errors = error.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        errors.forEach((err: string) => toast.error(err));
      } else {
        toast.error(message);
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handleRequestPrimaryEmailOtp = async () => {
    if (!primaryEmailInput) { toast.error(t('profile.primaryEmailRequired')); return; }
    setSendingPrimaryOtp(true);
    try {
      const res = await merchantApi.post('/merchants/profile/primary-email/request', { email: primaryEmailInput });
      setPrimaryEmailOtpSent(true);
      setPrimaryEmailCooldown(60);
      const timer = setInterval(() => {
        setPrimaryEmailCooldown(prev => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
      }, 1000);
      toast.success(res.data.message || t('profile.primaryEmailSendSuccess'));
    } catch (error: any) {
      const cd = error.response?.data?.data?.cooldown_seconds;
      if (cd) { setPrimaryEmailCooldown(cd); }
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSendingPrimaryOtp(false);
    }
  };

  const handleVerifyPrimaryEmail = async () => {
    if (!primaryEmailOtp) { toast.error(t('profile.primaryEmailOtpRequired')); return; }
    setVerifyingPrimaryEmail(true);
    try {
      await merchantApi.post('/merchants/profile/primary-email/verify', { otp: primaryEmailOtp });
      toast.success(t('profile.primaryEmailVerifySuccess'));
      setPrimaryEmailOtpSent(false);
      setPrimaryEmailOtp('');
      loadProfile();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setVerifyingPrimaryEmail(false);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setFormData({
      name: profile?.name || '',
      company_name: profile?.company_name || '',
      phone: profile?.phone || '',
      usdt_address: profile?.usdt_address || '',
      usdt_network: profile?.usdt_network || 'TRC20'
    });
  };

  // Password strength indicator
  const getPasswordStrength = (password: string) => {
    let score = 0;
    if (password.length >= 10) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++;
    return score;
  };

  const strengthLabels = ['', t('profile.strengthVeryWeak'), t('profile.strengthWeak'), t('profile.strengthMedium'), t('profile.strengthGood'), t('profile.strengthStrong')];
  const strengthColors = ['', 'bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const isEmployee = merchant?.role === 'employee' || merchant?.type === 'employee';

  return (
    <div className="w-full max-w-full mx-auto">
      <UiPageHeader icon={faUser} title={t('profile.title')} subtitle={t('profile.desc')} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 text-center">
            {/* Avatar */}
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center text-white text-4xl font-bold mx-auto mb-4 shadow-lg"
              style={{ background: 'var(--theme-primary-gradient)' }}
            >
              {(profile?.name || 'M').charAt(0).toUpperCase()}
            </div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-slate-100">{profile?.name}</h2>
            <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">{profile?.email}</p>

            {/* Badge */}
            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                 style={{
                   background: 'var(--theme-primary-light)',
                   color: 'var(--theme-primary)'
                 }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse"
                    style={{ background: 'var(--theme-primary)' }}></span>
              {isEmployee ? 'Employee' : 'Merchant'}
            </div>

            {/* Quick Info */}
            <div className="mt-6 space-y-3 text-left">
              <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-300">
                <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 dark:text-slate-500 w-4" />
                <span className="truncate">{profile?.email}</span>
              </div>
              {profile?.phone && (
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-300">
                  <FontAwesomeIcon icon={faPhone} className="text-gray-400 dark:text-slate-500 w-4" />
                  <span>{profile.phone}</span>
                </div>
              )}
              {profile?.company_name && (
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-300">
                  <FontAwesomeIcon icon={faBuilding} className="text-gray-400 dark:text-slate-500 w-4" />
                  <span>{profile.company_name}</span>
                </div>
              )}
              {profile?.fee_percentage !== undefined && profile?.fee_percentage !== null && (
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-300">
                  <FontAwesomeIcon icon={faPercent} className="text-gray-400 dark:text-slate-500 w-4" />
                  <span>{t('profile.feePercentage')} {parseFloat(profile.fee_percentage).toFixed(2)}%</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-slate-300">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 dark:text-slate-500 w-4" />
                <span>{t('profile.memberSince')} {new Date(profile?.created_at).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Form + Password */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Edit Section */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faUser} className="text-gray-400 dark:text-slate-500" />
                {t('profile.personalInfo')}
              </h3>
              {!isEmployee && !editing && (
                <UiButton icon={faPen} onClick={() => setEditing(true)}>
                  {t('common.edit')}
                </UiButton>
              )}
            </div>

            <form onSubmit={handleSaveProfile} className="p-6">
              <div className="space-y-4">
                {/* Email (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-gray-400 dark:text-slate-500" />
                    {t('profile.email')}
                  </label>
                  <input
                    type="email"
                    value={profile?.email || ''}
                    disabled
                    className="w-full px-4 py-3 border border-gray-200 dark:border-slate-700 rounded-xl bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">{t('profile.emailCannotChange')}</p>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">{t('profile.name')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={!editing}
                    className={inputClass(editing)}
                  />
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                    <FontAwesomeIcon icon={faBuilding} className="mr-2 text-gray-400 dark:text-slate-500" />
                    {t('profile.companyName')}
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    disabled={!editing}
                    placeholder={t('profile.notSpecified')}
                    className={inputClass(editing)}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                    <FontAwesomeIcon icon={faPhone} className="mr-2 text-gray-400 dark:text-slate-500" />
                    {t('profile.phone')}
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                    disabled={!editing}
                    placeholder={t('profile.notSpecified')}
                    maxLength={10}
                    className={inputClass(editing)}
                  />
                </div>

                {/* USDT Wallet */}
                <div className="pt-2 border-t border-gray-100 dark:border-slate-700">
                  <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <FontAwesomeIcon icon={faCoins} className="text-green-500" />
                    {t('profile.usdtWalletLabel')}
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">USDT Address</label>
                      <input
                        type="text"
                        value={formData.usdt_address}
                        onChange={(e) => setFormData({ ...formData, usdt_address: e.target.value })}
                        disabled={!editing}
                        placeholder={t('profile.usdtAddressPlaceholder')}
                        className={`${inputClass(editing)} font-mono text-sm`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Network</label>
                      <select
                        value={formData.usdt_network}
                        onChange={(e) => setFormData({ ...formData, usdt_network: e.target.value })}
                        disabled={!editing}
                        className={inputClass(editing)}
                      >
                        <option value="TRC20">TRC20 (Tron)</option>
                        <option value="ERC20">ERC20 (Ethereum)</option>
                        <option value="BEP20">BEP20 (BSC)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {editing && (
                <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-slate-700">
                  <UiButton type="submit" icon={faSave} loading={saving}>
                    {saving ? t('common.saving') : t('common.save')}
                  </UiButton>
                  <UiButton type="button" variant="secondary" icon={faTimes} onClick={cancelEdit}>
                    {t('common.cancel')}
                  </UiButton>
                </div>
              )}
            </form>
          </div>

          {/* Primary Email Section — commented out, not yet available for change */}
           {!isEmployee && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700">
                <h3 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                  <FontAwesomeIcon icon={faShieldAlt} className="text-blue-500" />
                  {t('profile.primaryEmailSection')}
                </h3>
              </div>
              <div className="p-6 space-y-4">
                {primaryEmail ? (
                  <div className={`flex items-center gap-3 p-3 rounded-xl border ${primaryEmailVerified ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'}`}>
                    <FontAwesomeIcon icon={primaryEmailVerified ? faCheckCircle : faExclamationTriangle} className={primaryEmailVerified ? 'text-green-500' : 'text-amber-500'} />
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-slate-100">{primaryEmail}</p>
                      <p className={`text-xs ${primaryEmailVerified ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                        {primaryEmailVerified ? t('profile.primaryEmailVerified') : t('profile.primaryEmailNotVerified')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500" />
                    <p className="text-sm text-red-700 dark:text-red-400">{t('profile.primaryEmailNotSet')}</p>
                  </div>
                )}
                {!primaryEmailVerified && <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                    {t('profile.primaryEmailSetLabel')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={primaryEmailInput}
                      onChange={e => { setPrimaryEmailInput(e.target.value); setPrimaryEmailOtpSent(false); setPrimaryEmailOtp(''); }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleRequestPrimaryEmailOtp(); } }}
                      placeholder="email@example.com"
                      className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleRequestPrimaryEmailOtp}
                      disabled={sendingPrimaryOtp || primaryEmailCooldown > 0 || !primaryEmailInput}
                      className="px-4 py-2.5 text-white text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      style={{ background: 'var(--theme-primary-gradient)' }}
                    >
                      {sendingPrimaryOtp
                        ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        : primaryEmailCooldown > 0 ? `${t('common.wait')} ${primaryEmailCooldown}s` : t('profile.primaryEmailSendOtp')}
                    </button>
                  </div>
                </div>}
                {/* Change primary email form — uncomment when ready
                {primaryEmailVerified && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">เปลี่ยน Email หลัก</label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={primaryEmailInput}
                        onChange={e => { setPrimaryEmailInput(e.target.value); setPrimaryEmailOtpSent(false); setPrimaryEmailOtp(''); }}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleRequestPrimaryEmailOtp(); } }}
                        placeholder="email@example.com"
                        className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent text-sm"
                      />
                      <button
                        type="button"
                        onClick={handleRequestPrimaryEmailOtp}
                        disabled={sendingPrimaryOtp || primaryEmailCooldown > 0 || !primaryEmailInput}
                        className="px-4 py-2.5 text-white text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        style={{ background: 'var(--theme-primary-gradient)' }}
                      >
                        {sendingPrimaryOtp
                          ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                          : primaryEmailCooldown > 0 ? `${t('common.wait')} ${primaryEmailCooldown}s` : t('profile.primaryEmailSendOtp')}
                      </button>
                    </div>
                  </div>
                )}
                */}
                {primaryEmailOtpSent && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">{t('profile.primaryEmailOtpLabel')}</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={primaryEmailOtp}
                        onChange={e => setPrimaryEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        onKeyDown={e => { if (e.key === 'Enter' && primaryEmailOtp.length === 6) { e.preventDefault(); handleVerifyPrimaryEmail(); } }}
                        placeholder="000000"
                        maxLength={6}
                        autoFocus
                        className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-center text-xl font-bold tracking-widest focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400"
                      />
                      <button
                        type="button"
                        onClick={handleVerifyPrimaryEmail}
                        disabled={verifyingPrimaryEmail || primaryEmailOtp.length !== 6}
                        className="px-4 py-2.5 text-white text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: 'var(--theme-primary-gradient)' }}
                      >
                        {verifyingPrimaryEmail ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : t('profile.primaryEmailVerifyBtn')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )} 

          {/* Password Section */}
          {!isEmployee && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                  <FontAwesomeIcon icon={faLock} className="text-gray-400 dark:text-slate-500" />
                  {t('profile.password')}
                </h3>
                {!showPasswordForm && (
                  <UiButton variant="secondary" icon={faLock} onClick={() => setShowPasswordForm(true)}>
                    {t('profile.changePassword')}
                  </UiButton>
                )}
              </div>

              {!showPasswordForm ? (
                <div className="p-6">
                  <div className="flex items-center gap-3 text-gray-500 dark:text-slate-400">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-700 dark:text-slate-200">{t('profile.passwordSet')}</p>
                      <p className="text-sm text-gray-400 dark:text-slate-500">{t('profile.passwordRecommend')}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleChangePassword} className="p-6">
                  {/* Password Requirements Info */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-5">
                    <div className="flex items-start gap-3">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 mt-0.5" />
                      <div className="text-sm">
                        <strong className="text-amber-800 dark:text-amber-300">{t('profile.passwordRequirements')}</strong>
                        <ul className="text-amber-700 dark:text-amber-400 mt-1 space-y-0.5">
                          <li>- {t('profile.passwordMinLength')}</li>
                          <li>- {t('profile.passwordComplexity')}</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Current Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">{t('profile.currentPassword')}</label>
                      <div className="relative">
                        <input
                          type={showPasswords.current ? 'text' : 'password'}
                          value={passwordData.current_password}
                          onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                          required
                          className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 pr-12"
                          placeholder={t('profile.currentPasswordPlaceholder')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 p-1"
                        >
                          <FontAwesomeIcon icon={showPasswords.current ? faEyeSlash : faEye} />
                        </button>
                      </div>
                    </div>

                    {/* New Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">{t('profile.newPassword')}</label>
                      <div className="relative">
                        <input
                          type={showPasswords.new_password ? 'text' : 'password'}
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                          required
                          className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 pr-12"
                          placeholder={t('profile.newPasswordPlaceholder')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, new_password: !showPasswords.new_password })}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 p-1"
                        >
                          <FontAwesomeIcon icon={showPasswords.new_password ? faEyeSlash : faEye} />
                        </button>
                      </div>
                      {/* Strength Indicator */}
                      {passwordData.new_password && (
                        <div className="mt-2">
                          <div className="flex gap-1 mb-1">
                            {[1, 2, 3, 4, 5].map(i => (
                              <div
                                key={i}
                                className={`h-1.5 flex-1 rounded-full transition-colors ${
                                  i <= getPasswordStrength(passwordData.new_password)
                                    ? strengthColors[getPasswordStrength(passwordData.new_password)]
                                    : 'bg-gray-200 dark:bg-slate-600'
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-slate-400">
                            {t('profile.strength')} {strengthLabels[getPasswordStrength(passwordData.new_password)]}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">{t('profile.confirmPassword')}</label>
                      <div className="relative">
                        <input
                          type={showPasswords.confirm ? 'text' : 'password'}
                          value={passwordData.confirm_password}
                          onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                          required
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 pr-12 dark:bg-slate-700 dark:text-slate-100 ${
                            passwordData.confirm_password && passwordData.confirm_password !== passwordData.new_password
                              ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                              : 'border-gray-300 dark:border-slate-600'
                          }`}
                          placeholder={t('profile.confirmPasswordPlaceholder')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 p-1"
                        >
                          <FontAwesomeIcon icon={showPasswords.confirm ? faEyeSlash : faEye} />
                        </button>
                      </div>
                      {passwordData.confirm_password && passwordData.confirm_password !== passwordData.new_password && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-1">{t('profile.passwordNotMatch')}</p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-slate-700">
                    <UiButton
                      type="submit"
                      icon={faLock}
                      loading={changingPassword}
                      disabled={passwordData.new_password !== passwordData.confirm_password}
                    >
                      {changingPassword ? t('profile.changingPassword') : t('profile.changePassword')}
                    </UiButton>
                    <UiButton
                      type="button"
                      variant="secondary"
                      icon={faTimes}
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                      }}
                    >
                      {t('common.cancel')}
                    </UiButton>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      <ActionOtpModal
        isOpen={showProfileOtp}
        onClose={() => setShowProfileOtp(false)}
        onVerified={handleSaveProfileVerified}
        action="profile_update"
        title="ยืนยันการแก้ไขข้อมูล"
        description="กรุณากรอก OTP เพื่อยืนยันการแก้ไขข้อมูลส่วนตัว"
      />

      <ActionOtpModal
        isOpen={showPasswordOtp}
        onClose={() => setShowPasswordOtp(false)}
        onVerified={handleChangePasswordVerified}
        action="password_change"
        title="ยืนยันการเปลี่ยนรหัสผ่าน"
        description="กรุณากรอก OTP เพื่อยืนยันการเปลี่ยนรหัสผ่าน"
      />
    </div>
  );
};

export default ProfilePage;
